import React, { useState } from 'react';

import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CountryCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  CountrySelect,
  Form,
  FormField,
  IconAddCandidateOutline,
  SlideIn,
} from '@/components';
import { CircularIconContainer } from '@/components/Icons/IconContainer/';
import { zu } from '@/lib/zod/zod-utils';
import {
  DepartmentType,
  type SurveyParticipationBasicDto,
} from '@/services/api';

import { departmentLabel } from '../board/DepartmentLabel';
import { useInviteSurveyParticipantMutation } from './useAdminSurveyQueries';

interface AddOrEditSurveyParticipantModalProps {
  isOpen: boolean;
  onClose: () => void;
  surveyId: string;
  participant?: SurveyParticipationBasicDto;
}

const zFormFieldsParticipantAdd = () =>
  z.object({
    name: zu.string.nonEmpty(z.string(), {
      required_error: 'Name is required',
    }),
    surname: zu.string.nonEmpty(z.string(), {
      required_error: 'Surname is required',
    }),
    email: zu.string.email(z.string(), {
      required_error: 'Email is required',
      invalid_type_error: 'Invalid email address',
    }),
    phone: z.string().optional(),
    department: z.string().optional(),
  });

type FormFieldsParticipantAdd = z.infer<
  ReturnType<typeof zFormFieldsParticipantAdd>
>;

export const AddOrEditSurveyParticipantModal: React.FC<
  AddOrEditSurveyParticipantModalProps
> = ({ isOpen, onClose, surveyId, participant }) => {
  const toast = useToast();
  const { t } = useTranslation(['common', 'survey', 'candidate', 'components']);
  const [country, setCountry] = useState<CountryCode>();

  const inviteParticipantMutation = useInviteSurveyParticipantMutation({
    onSuccess() {
      toast({
        title: t('survey:participantInvited', {
          defaultValue: 'Participant invited successfully',
        }),
        status: 'success',
      });
      form.reset();
      onClose();
    },
    onError() {
      toast({
        title: t('survey:participantInviteError', {
          defaultValue: 'Failed to invite participant',
        }),
        status: 'error',
      });
    },
  });

  const form = useForm<FormFieldsParticipantAdd>({
    mode: 'onBlur',
    resolver: zodResolver(zFormFieldsParticipantAdd()),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      department: participant?.department ?? '',
    },
  });

  const onSubmit: SubmitHandler<FormFieldsParticipantAdd> = async (data) => {
    await inviteParticipantMutation.mutateAsync({
      surveyId,
      body: {
        name: data.name,
        surname: data.surname,
        email: data.email,
        phone: data.phone
          ? (parsePhoneNumberFromString(data.phone, country ?? 'TR')?.format(
              'E.164'
            ) ?? undefined)
          : undefined,
        department: data.department ?? undefined,
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        form.reset();
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent w="420px" borderRadius="xl" pt={6} alignItems="center">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />
        <ModalHeader justifyItems="center">
          <VStack>
            <CircularIconContainer icon={IconAddCandidateOutline} />
            <Text>{t('survey:inviteParticipant')}</Text>
          </VStack>
        </ModalHeader>
        <ModalBody w="full" pb={6}>
          <SlideIn>
            <Form {...form} onSubmit={onSubmit}>
              <Stack spacing={4}>
                <FormField
                  type="text"
                  label={t('common:information.name')}
                  control={form.control}
                  name="name"
                  optionalityHint="required"
                  placeholder={t('common:information.namePlaceholder')}
                />
                <FormField
                  type="text"
                  label={t('common:information.surname')}
                  control={form.control}
                  name="surname"
                  optionalityHint="required"
                  placeholder={t('common:information.surnamePlaceholder')}
                />
                <FormField
                  type="email"
                  label={t('common:information.email')}
                  control={form.control}
                  name="email"
                  optionalityHint="required"
                  placeholder={t('common:information.emailPlaceholder')}
                />
                <Flex columnGap={3} alignItems="center">
                  <Stack minWidth={100} rowGap={0.5}>
                    <CountrySelect
                      size="lg"
                      value={country}
                      onCountryChange={setCountry as ExplicitAny}
                    />
                  </Stack>

                  <FormField
                    type="phone-number"
                    name="phone"
                    control={form.control}
                    placeholder={t('common:information.phonePlaceholder')}
                    country={country}
                    withCountryCallingCode={false}
                    international
                    useNationalFormatForDefaultCountryValue
                  />
                </Flex>
                <FormField
                  type="select"
                  label={t('components:board.department')}
                  control={form.control}
                  name="department"
                  options={Object.values(DepartmentType).map((department) => ({
                    label: departmentLabel(department),
                    value: department,
                  }))}
                  placeholder={t('common:actions.pleaseSelect')}
                  optionalityHint="optional"
                  menuPlacement="top"
                />

                <HStack justify="end" pt={4}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      form.reset();
                      onClose();
                    }}
                  >
                    {t('common:actions.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="@primary"
                    isLoading={inviteParticipantMutation.isPending}
                  >
                    {t('survey:inviteParticipant')}
                  </Button>
                </HStack>
              </Stack>
            </Form>
          </SlideIn>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
